import { css, cx } from "@emotion/css";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiUser,
  FiShoppingCart,
  FiList,
  FiLogOut,
  FiCreditCard,
} from "react-icons/fi";
import { IoTicketOutline, IoWalletOutline } from "react-icons/io5";
import { useAuth } from "../../../auth/hooks/use-auth";
import type { Profile } from "../../../../api/services/profile/types";
import type { HTMLAttributes, CSSProperties } from "react";
import BalanceAdditionButton from "../../../../shared/components/balance-addition-button/balance-addition-button";
import { useGetUserBalance } from "../../hooks/profile-api.hooks";

/* Styles */
const profileCard = css({
  backgroundColor: "var(--primary-color)",
  borderRadius: "var(--spacing-sm)",
  padding: "var(--spacing-lg)",
  boxShadow: "var(--shadow-200)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-xl)",
});

const profileCardUser = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-md)",
});

const profileCardAvatar = css({
  width: "64px",
  height: "64px",
  borderRadius: "50%",
  overflow: "hidden",
  flexShrink: 0,
});

const profileCardAvatarImg = css({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const profileCardAvatarPlaceholder = css({
  width: "100%",
  height: "100%",
  backgroundColor: "var(--pc-200)",
  color: "var(--pc-600)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "var(--fs-500)",
  fontWeight: 600,
});

const profileCardUserInfo = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-2xs)",
});

const profileCardName = css({
  fontSize: "var(--fs-400)",
  fontWeight: 600,
  color: "var(--g-800)",
});

const profileCardEmail = css({
  fontSize: "var(--fs-350)",
  color: "var(--g-500)",
  maxWidth: "200px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-all",
});

const profileCardBalance = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-2xs)",
  color: "var(--pc-600)",
  fontSize: "var(--fs-350)",
  fontWeight: 600,
  marginTop: "var(--spacing-2xs)",
  "& svg": {
    color: "var(--pc-500)",
  },
});

const balanceAddBtnStyle = css({
  marginLeft: "var(--spacing-xs)",
  width: 28,
  height: 28,
  fontSize: 18,
});

const profileCardMenu = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-xs)",
  "@media (max-width: 96.7rem)": {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "var(--spacing-sm)",
  },
  "@media (min-width: 96.8rem)": {
    flexDirection: "column",
  },
  "@media (max-width: 480px)": {
    gridTemplateColumns: "1fr",
  },
});

const profileCardMenuItem = css({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-xs)",
  padding: "var(--spacing-sm)",
  border: "none",
  background: "none",
  borderRadius: "var(--spacing-xs)",
  color: "var(--g-700)",
  fontSize: "var(--fs-350)",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "var(--g-100)",
    color: "var(--pc-600)",
  },
  "@media (max-width: 480px)": {
    padding: "var(--spacing-xs)",
    fontSize: "var(--fs-300)",
  },
});

const profileCardMenuItemLogout = css({
  marginTop: "var(--spacing-xs)",
  color: "var(--g-500)",
  "&:hover": {
    color: "#ef4444",
    backgroundColor: "#fee2e2",
  },
  "&:disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  "@media (max-width: 96.7rem)": {
    gridColumn: "span 2",
    marginTop: 0,
  },
  "@media (max-width: 480px)": {
    gridColumn: "span 1",
  },
});

const profileCardMobile = css({
  "@media (max-width: 96.7rem)": {
    padding: "var(--spacing-md)",
  },
  "@media (max-width: 480px)": {
    padding: "var(--spacing-sm)",
  },
});

const profileCardUserMobile = css({
  "@media (max-width: 400px)": {
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
});

const profileCardEmailMobile = css({
  "@media (max-width: 400px)": {
    maxWidth: "100%",
  },
});

/* Component Props */
export interface ProfileCardProps extends HTMLAttributes<HTMLDivElement> {
  user: Profile;
  style?: CSSProperties;
}

export function ProfileCard({
  user,
  className,
  style,
  ...props
}: ProfileCardProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: balanceData } = useGetUserBalance();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Format balance to BRL currency (R$ XX.XX)
  const formatBalance = (balance?: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(balance || 0);
  };

  const handleLogout = () => {
    setIsLoggingOut(true);
    logout();
    // Note: Navigation is handled by the auth context
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const menuItems = [
    {
      icon: <FiUser size={20} />,
      label: "Meus dados",
      onClick: () => handleNavigate("/perfil"),
    },
    {
      icon: <FiShoppingCart size={20} />,
      label: "Minhas compras",
      onClick: () => handleNavigate("/perfil/compras"),
    },
    {
      icon: <IoTicketOutline size={20} />,
      label: "Meus ingressos",
      onClick: () => handleNavigate("/perfil/ingressos"),
    },
    {
      icon: <FiCreditCard size={20} />,
      label: "Métodos de pagamento",
      onClick: () => handleNavigate("/perfil/pagamento"),
    },
    {
      icon: <FiList size={20} />,
      label: "Lista de espera",
      onClick: () => handleNavigate("/perfil/lista-espera"),
    },
  ];

  // Get avatar URL if available
  const avatarUrl = user.profilePicId
    ? `${import.meta.env.VITE_API_BASE_URL}/uploads/${user.profilePicId}.${
        user.profilePicExtension
      }`
    : null;

  return (
    <div
      className={cx(profileCard, profileCardMobile, className)}
      style={style}
      {...props}
    >
      <div className={cx(profileCardUser, profileCardUserMobile)}>
        <div className={profileCardAvatar}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={`Foto de ${user.name || "usuário"}`}
              className={profileCardAvatarImg}
            />
          ) : (
            <div className={profileCardAvatarPlaceholder}>
              {user.name?.charAt(0).toUpperCase() || "U"}
            </div>
          )}
        </div>
        <div className={profileCardUserInfo}>
          <h2 className={profileCardName}>{user.name || "Usuário"}</h2>
          <p className={cx(profileCardEmail, profileCardEmailMobile)}>
            {user.email || "Email não informado"}
          </p>
          <div className={profileCardBalance}>
            <IoWalletOutline size={16} />
            <span>{formatBalance(balanceData?.balance)}</span>
            <BalanceAdditionButton className={balanceAddBtnStyle} />
          </div>
        </div>
      </div>

      <nav className={profileCardMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            className={profileCardMenuItem}
            onClick={item.onClick}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
        <button
          className={cx(profileCardMenuItem, profileCardMenuItemLogout)}
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          <FiLogOut size={20} />
          <span>{isLoggingOut ? "Saindo..." : "Sair"}</span>
        </button>
      </nav>
    </div>
  );
}

export default ProfileCard;
